import { forwardRef, useState, useEffect, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import Spinner from '../spinner/spinner';
import closeicon from './closeIcon.svg';
import searchicon from './searchIcon.svg';
import styles from './helpCenterSearch.module.scss';

function CloseIcon(props) {
  return (
    <div {...props}>
      <img
        src={closeicon.src}
        loading="lazy"
        alt="close icon"
        height={closeicon.height ?? 18}
        width={closeicon.width ?? 18}
      />
    </div>
  );
}

function SearchIcon(props) {
  return (
    <div {...props}>
      <img
        src={searchicon.src}
        loading="lazy"
        alt="search icon"
        height={searchicon.height ?? 18}
        width={searchicon.width ?? 18}
      />
    </div>
  );
}

function LoadingIcon(props) {
  return (
    <div {...props}>
      <Spinner showSpinner />
    </div>
  );
}

const HelpCenterSearch = forwardRef(
  (
    {
      placeholder = 'Type a question or search keyword',
      onChange,
      hasError,
      onEnter,
      isLoading,
      showCloseIcon,
      showSearchIcon,
      onIconClick,
      className,
      defaultValue = false,
    },
    ref
  ) => {
    const [inFocus, setinFocus] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
      if (defaultValue) setSearchTerm('');
    }, [defaultValue]);

    useEffect(() => {
      if (searchTerm === '') {
        setIsEmpty(true);
      } else {
        setIsEmpty(false);
      }
    }, [searchTerm]);

    useImperativeHandle(ref, () => ({
      clear() {
        setSearchTerm('');
      },
    }));

    const handleOnChange = (event) => {
      const { value } = event.currentTarget;
      const maxKeywords = 25;
      const keyWords = value.trim().split(/\s+/);
      const changeSearchTerm = (term) => {
        setSearchTerm(term);
        if (onChange) onChange(term);
      };
      // Check if value consists only of whitespace characters
      const isEmptySpace = /^\s*$/.test(value);
      if (!isEmptySpace && keyWords.length <= maxKeywords) {
        changeSearchTerm(value);
      } else {
        const slicedValue = keyWords.slice(0, maxKeywords).join(' ');
        changeSearchTerm(slicedValue);
      }
    };

    const handleIconClick = (event) => {
      const { currentTarget } = event;
      if (onIconClick) {
        onIconClick(currentTarget, setSearchTerm);
      } else {
        setSearchTerm('');
      }
    };

    const handleOnBlur = () => setinFocus(false);
    const handleOnFocus = () => setinFocus(true);

    const handleOnKeyDown = (event) => {
      const { key, currentTarget } = event;
      if (key === 'Enter' && currentTarget.value && onEnter)
        onEnter(currentTarget.value);
    };

    const getIcon = (
      <>
        {isEmpty && !showSearchIcon && !showCloseIcon && !isLoading && (
          <SearchIcon
            data-testid="input-icon"
            className={`${styles.icon} icon_search`}
            onClick={handleIconClick}
          />
        )}

        {!isEmpty && !showSearchIcon && !showCloseIcon && !isLoading && (
          <CloseIcon
            data-testid="input-icon"
            className={`${styles.icon} icon_close`}
            onClick={handleIconClick}
          />
        )}

        {showCloseIcon && (
          <CloseIcon
            data-testid="input-icon"
            className={`${styles.icon} icon_close`}
            onClick={handleIconClick}
          />
        )}

        {isLoading && !showCloseIcon && (
          <LoadingIcon
            data-testid="input-icon"
            className={`${styles.icon} icon_loading`}
            onClick={handleIconClick}
          />
        )}

        {showSearchIcon && !showCloseIcon && !isLoading && (
          <SearchIcon
            data-testid="input-icon"
            className={`${styles.icon} icon_search`}
            onClick={handleIconClick}
          />
        )}
      </>
    );

    const wrapperHasErrorClassName = hasError ? styles.errorWrapper : '';
    const wrapperInFocusClassName = inFocus ? styles.focusWrapper : '';
    const wrapperCompoundClassName = `${styles.wrapper} ${wrapperHasErrorClassName} ${wrapperInFocusClassName} ${className}`;

    return (
      <div className={wrapperCompoundClassName} ref={ref}>
        {hasError && <span className={styles.errorText}> {hasError}</span>}
        {getIcon}
        <input
          data-testid="search-input"
          label="search input"
          className={styles.input}
          type="text"
          value={searchTerm}
          placeholder={placeholder}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onKeyDown={handleOnKeyDown}
          onChange={handleOnChange}
        />
      </div>
    );
  }
);

HelpCenterSearch.displayName = HelpCenterSearch;

HelpCenterSearch.propTypes = {
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onEnter: PropTypes.func,
  isLoading: PropTypes.bool,
  showCloseIcon: PropTypes.bool,
  showSearchIcon: PropTypes.bool,
  defaultValue: PropTypes.bool,
};

export default HelpCenterSearch;
